// =============================================================================
// MeuDriver — Admin Instructor Management
// Full list: search, filter, paginate, approve/suspend, view profile
// =============================================================================

import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { formatDate } from '@/lib/utils'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import type { InstructorStatus, InstructorWithProfile } from '@/types/database'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Search, ChevronLeft, ChevronRight, Star, CheckSquare } from 'lucide-react'
import { AdminCnhReviewButton } from '@/components/admin/AdminCnhReviewButton'
import { AdminIdentityDocReviewButton } from '@/components/admin/AdminIdentityDocReviewButton'
import { useToggleFeatured } from '@/hooks/useAdminFeaturedInstructors'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 10

type TabFilter = 'all' | 'approved' | 'pending' | 'suspended'

interface ConfirmDialogState {
  open: boolean
  instructorId: string
  instructorName: string
  action: 'approve' | 'suspend'
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  const first = words[0]?.[0] ?? ''
  const last = words.length > 1 ? words[words.length - 1][0] ?? '' : ''
  return (first + last).toUpperCase()
}

function statusBadge(status: InstructorStatus) {
  switch (status) {
    case 'approved':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
          Aprovado
        </Badge>
      )
    case 'pending':
      return (
        <Badge className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">
          Aguardando
        </Badge>
      )
    case 'suspended':
      return (
        <Badge variant="destructive">Suspenso</Badge>
      )
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AdminInstructors() {
  const [instructors, setInstructors] = useState<InstructorWithProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [activeTab, setActiveTab] = useState<TabFilter>('all')
  const [page, setPage] = useState(0)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    instructorId: '',
    instructorName: '',
    action: 'approve',
  })
  const [actionLoading, setActionLoading] = useState(false)
  const toggleFeatured = useToggleFeatured()
  const [suspensionReason, setSuspensionReason] = useState('')
  const [bulkSuspendDialogOpen, setBulkSuspendDialogOpen] = useState(false)
  const [bulkSuspendReason, setBulkSuspendReason] = useState('')

  // ── debounce search ─────────────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(0)
    }, 350)
    return () => clearTimeout(timer)
  }, [search])

  // ── fetch instructors ───────────────────────────────────────────────────────

  const fetchInstructors = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('instructors')
        .select('*, profile:profiles!instructors_profile_id_fkey(*)', { count: 'exact' })

      if (activeTab !== 'all') {
        const statusMap: Record<TabFilter, InstructorStatus> = {
          all: 'approved',
          approved: 'approved',
          pending: 'pending',
          suspended: 'suspended',
        }
        query = query.eq('status', statusMap[activeTab])
      }

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (error) throw error

      // Client-side search filter on name / city
      let filtered = (data as unknown as InstructorWithProfile[]) ?? []
      if (debouncedSearch.trim()) {
        const q = debouncedSearch.toLowerCase()
        filtered = filtered.filter(
          (i) =>
            i.profile?.full_name?.toLowerCase().includes(q) ||
            i.profile?.city?.toLowerCase().includes(q),
        )
      }

      setInstructors(filtered)
      setTotalCount(count ?? 0)
    } catch {
      toast.error('Erro ao carregar instrutores')
    } finally {
      setLoading(false)
    }
  }, [activeTab, page, debouncedSearch])

  useEffect(() => {
    fetchInstructors()
  }, [fetchInstructors])

  // ── actions ─────────────────────────────────────────────────────────────────

  function openConfirm(
    instructor: InstructorWithProfile,
    action: 'approve' | 'suspend',
  ) {
    setSuspensionReason('')
    setConfirmDialog({
      open: true,
      instructorId: instructor.id,
      instructorName: instructor.profile?.full_name ?? 'Instrutor',
      action,
    })
  }

  async function handleConfirmAction() {
    setActionLoading(true)
    const newStatus: InstructorStatus =
      confirmDialog.action === 'approve' ? 'approved' : 'suspended'
    try {
      const { error } = await supabase
        .from('instructors')
        .update({ status: newStatus })
        .eq('id', confirmDialog.instructorId)

      if (error) throw error

      if (confirmDialog.action === 'suspend' && suspensionReason.trim()) {
        console.info('[AdminInstructors] Suspension reason:', suspensionReason.trim())
      }

      toast.success(
        confirmDialog.action === 'approve'
          ? 'Instrutor aprovado com sucesso!'
          : 'Instrutor suspenso.',
      )
      setSuspensionReason('')
      setConfirmDialog((prev) => ({ ...prev, open: false }))
      await fetchInstructors()
    } catch {
      toast.error('Erro ao atualizar status do instrutor')
    } finally {
      setActionLoading(false)
    }
  }

  // ── bulk actions ─────────────────────────────────────────────────────────────

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    if (selectedIds.size === instructors.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(instructors.map((i) => i.id)))
    }
  }

  async function handleBulkAction(action: 'approve' | 'suspend', reason?: string) {
    if (selectedIds.size === 0) return
    setBulkLoading(true)
    const newStatus: InstructorStatus = action === 'approve' ? 'approved' : 'suspended'
    try {
      const { error } = await supabase
        .from('instructors')
        .update({ status: newStatus })
        .in('id', Array.from(selectedIds))
      if (error) throw error
      if (action === 'suspend' && reason?.trim()) {
        console.info('[AdminInstructors] Bulk suspension reason:', reason.trim())
      }
      toast.success(`${selectedIds.size} instrutor(es) ${action === 'approve' ? 'aprovado(s)' : 'suspenso(s)'}.`)
      setSelectedIds(new Set())
      await fetchInstructors()
    } catch {
      toast.error('Erro ao aplicar ação em massa.')
    } finally {
      setBulkLoading(false)
    }
  }

  // ── pagination ──────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Gerenciar Instrutores</h1>
        <p className="mt-1 text-muted-foreground">
          Busque, aprove, suspenda e visualize perfis de instrutores.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por nome ou cidade..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Tabs
          value={activeTab}
          onValueChange={(v) => {
            setActiveTab(v as TabFilter)
            setPage(0)
          }}
        >
          <TabsList>
            <TabsTrigger value="all">Todos</TabsTrigger>
            <TabsTrigger value="approved">Aprovados</TabsTrigger>
            <TabsTrigger value="pending">Aguardando</TabsTrigger>
            <TabsTrigger value="suspended">Suspensos</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-2.5">
          <CheckSquare className="h-4 w-4 text-brand-600 shrink-0" />
          <span className="text-sm font-medium text-brand-800">
            {selectedIds.size} selecionado(s)
          </span>
          <div className="flex gap-2 ml-auto">
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white text-xs h-7"
              disabled={bulkLoading}
              onClick={() => handleBulkAction('approve')}
            >
              Aprovar selecionados
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="text-xs h-7"
              disabled={bulkLoading}
              onClick={() => { setBulkSuspendReason(''); setBulkSuspendDialogOpen(true) }}
            >
              Suspender selecionados
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-xs h-7"
              onClick={() => setSelectedIds(new Set())}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle className="text-base">
            {totalCount > 0 ? `${totalCount} instrutor(es) encontrado(s)` : 'Instrutores'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-4 p-6">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-md" />
              ))}
            </div>
          ) : instructors.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              Nenhum instrutor encontrado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-muted-foreground">
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === instructors.length && instructors.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="px-4 py-3 font-medium">Instrutor</th>
                    <th className="px-4 py-3 font-medium">Cidade</th>
                    <th className="px-4 py-3 font-medium">CNH / Doc</th>
                    <th className="px-4 py-3 font-medium">Avaliação</th>
                    <th className="px-4 py-3 font-medium">Total Aulas</th>
                    <th className="px-4 py-3 font-medium">Destaque</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Cadastrado em</th>
                    <th className="px-4 py-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {instructors.map((instructor) => {
                    const name = instructor.profile?.full_name ?? 'Sem nome'
                    const avatarUrl = instructor.profile?.avatar_url ?? undefined
                    return (
                      <tr
                        key={instructor.id}
                        className="hover:bg-muted/20 transition-colors"
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(instructor.id)}
                            onChange={() => toggleSelect(instructor.id)}
                            className="rounded"
                          />
                        </td>
                        {/* Avatar + Nome */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarImage src={avatarUrl} alt={name} />
                              <AvatarFallback className="text-xs font-semibold">
                                {getInitials(name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{name}</p>
                              <p className="text-xs text-muted-foreground">
                                {instructor.profile?.phone ?? '—'}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Cidade */}
                        <td className="px-4 py-3 text-muted-foreground">
                          {instructor.profile?.city ?? '—'}
                          {instructor.profile?.state
                            ? `, ${instructor.profile.state}`
                            : ''}
                        </td>

                        {/* CNH / Doc identidade */}
                        <td className="px-4 py-3 font-mono text-xs">
                          <div className="flex flex-col gap-1">
                            <span>{instructor.cnh_number ?? '—'}</span>
                            <AdminCnhReviewButton
                              instructorId={instructor.id}
                              instructorProfileId={instructor.profile_id}
                              cnhStatus={
                                (instructor as InstructorWithProfile & { cnh_status?: 'pending' | 'verified' | 'rejected' | null })
                                  .cnh_status ?? (instructor.cnh_verified ? 'verified' : null)
                              }
                              onUpdated={() => fetchInstructors()}
                            />
                            <AdminIdentityDocReviewButton
                              instructorId={instructor.id}
                              instructorProfileId={instructor.profile_id}
                              docStatus={
                                (instructor as InstructorWithProfile & { identity_doc_status?: 'not_submitted' | 'pending' | 'verified' | 'rejected' | null })
                                  .identity_doc_status ?? 'not_submitted'
                              }
                              onUpdated={() => fetchInstructors()}
                            />
                          </div>
                        </td>

                        {/* Avaliação */}
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1">
                            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                            {instructor.rating_avg.toFixed(1)}
                          </span>
                        </td>

                        {/* Total Aulas */}
                        <td className="px-4 py-3">{instructor.total_lessons}</td>

                        {/* Destaque */}
                        <td className="px-4 py-3">
                          <button
                            title={instructor.is_featured ? 'Remover destaque' : 'Marcar como destaque'}
                            disabled={toggleFeatured.isPending}
                            onClick={() =>
                              toggleFeatured.mutate({
                                id: instructor.id,
                                featured: !instructor.is_featured,
                              })
                            }
                            data-testid={`toggle-featured-${instructor.id}`}
                            className="flex items-center justify-center rounded-md p-1 hover:bg-yellow-50 transition-colors disabled:opacity-50"
                          >
                            <Star
                              className={
                                instructor.is_featured
                                  ? 'h-4 w-4 fill-yellow-400 text-yellow-400'
                                  : 'h-4 w-4 text-gray-300'
                              }
                            />
                          </button>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">{statusBadge(instructor.status)}</td>

                        {/* Cadastrado em */}
                        <td className="px-4 py-3 text-muted-foreground">
                          {formatDate(instructor.created_at)}
                        </td>

                        {/* Ações */}
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {instructor.status !== 'approved' && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2"
                                onClick={() => openConfirm(instructor, 'approve')}
                              >
                                Aprovar
                              </Button>
                            )}
                            {instructor.status !== 'suspended' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs h-7 px-2"
                                onClick={() => openConfirm(instructor, 'suspend')}
                              >
                                Suspender
                              </Button>
                            )}
                            {instructor.slug && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 px-2"
                                onClick={() =>
                                  window.open(`/instrutor/${instructor.slug}`, '_blank')
                                }
                              >
                                Ver perfil
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Próximo
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Suspend Dialog */}
      <Dialog open={bulkSuspendDialogOpen} onOpenChange={setBulkSuspendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspender {selectedIds.size} instrutor(es)</DialogTitle>
            <DialogDescription>
              Esta ação suspenderá <strong>{selectedIds.size}</strong> instrutor(es) simultaneamente.
              Eles ficarão impossibilitados de receber novas reservas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-suspension-reason">Motivo da suspensão (opcional)</Label>
            <Textarea
              id="bulk-suspension-reason"
              placeholder="Descreva o motivo da suspensão em massa..."
              value={bulkSuspendReason}
              onChange={(e) => setBulkSuspendReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSuspendDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={bulkLoading}
              onClick={async () => {
                setBulkSuspendDialogOpen(false)
                await handleBulkAction('suspend', bulkSuspendReason)
              }}
            >
              {bulkLoading ? 'Processando...' : 'Confirmar Suspensão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmDialog.action === 'approve'
                ? 'Aprovar Instrutor'
                : 'Suspender Instrutor'}
            </DialogTitle>
            <DialogDescription>
              {confirmDialog.action === 'approve' ? (
                <>
                  Confirma a aprovação de{' '}
                  <strong>{confirmDialog.instructorName}</strong>? Ele poderá
                  receber reservas na plataforma.
                </>
              ) : (
                <>
                  Confirma a suspensão de{' '}
                  <strong>{confirmDialog.instructorName}</strong>? Ele ficará
                  impossibilitado de receber novas reservas.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {confirmDialog.action === 'suspend' && (
            <div className="space-y-1.5">
              <Label htmlFor="suspension-reason">Motivo da suspensão (opcional)</Label>
              <Textarea
                id="suspension-reason"
                placeholder="Descreva o motivo da suspensão..."
                value={suspensionReason}
                onChange={(e) => setSuspensionReason(e.target.value)}
                rows={3}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
            >
              Cancelar
            </Button>
            <Button
              variant={confirmDialog.action === 'approve' ? 'default' : 'destructive'}
              className={
                confirmDialog.action === 'approve'
                  ? 'bg-green-600 hover:bg-green-700'
                  : ''
              }
              disabled={actionLoading}
              onClick={handleConfirmAction}
            >
              {actionLoading
                ? 'Processando...'
                : confirmDialog.action === 'approve'
                ? 'Confirmar Aprovação'
                : 'Confirmar Suspensão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
